import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../common-styles.css';
import './RoughToPolishPage.css';
import bannerImg from '../../assets/images/process/banner.png';

// Utility to format (static) date shown in design. Keeping static per screenshot.
const ARTICLE_DATE = 'August 09, 2025';

// All section + subsection anchors for generating the Table of Contents (also used for scroll spy)
interface TocItem { id: string; label: string; children?: TocItem[]; }
const TOC: TocItem[] = [
  { id: 'introduction', label: 'Introduction', children: [
    { id: 'timeless-allure', label: 'The Timeless Allure of Diamonds' },
    { id: 'what-is-cycle', label: 'What is the Rough to Polish Cycle?' }
  ]},
  { id: 'discovery', label: "Discovery: Unearthing Earth's Hidden Gems", children: [
    { id: 'where-found', label: 'Where Diamonds Are Found' },
    { id: 'role-of-mining', label: 'The Role of Mining in the Journey' }
  ]},
  { id: 'evaluation', label: 'Evaluation: Revealing the Stone’s True Promise', children: [
    { id: 'assessing-rough', label: 'How Gemologists Assess Rough Diamonds' },
    { id: 'preview-4cs', label: 'The 4Cs: A Preview of Quality' }
  ]},
  { id: 'planning', label: 'Planning: Designing Brilliance with Precision', children: [
    { id: 'imaging-modeling', label: 'Using 3D Imaging and Modeling' },
    { id: 'carat-vs-sparkle', label: 'Balancing Carat Retention and Sparkle' }
  ]},
  { id: 'cutting-polishing', label: 'Cutting & Polishing: Bringing the Diamond to Life', children: [
    { id: 'cleaving-bruting', label: 'Cleaving, Bruting, Faceting' },
    { id: 'role-of-artisans', label: 'The Role of Artisans in Final Brilliance' }
  ]},
  { id: 'certification', label: 'Certification & Presentation: Honoring the Final Form', children: [
    { id: 'grading-process', label: 'Diamond Grading and Certification Process' },
    { id: 'gem-to-jewelry', label: 'The Journey from Gem to Jewelry' }
  ]},
  { id: 'metaphor', label: 'The Diamond as a Metaphor', children: [
    { id: 'life-lessons', label: 'Life Lessons from the Rough to Polish Cycle' },
    { id: 'why-inspires', label: 'Why This Transformation Inspires Us All' }
  ]},
  { id: 'conclusion', label: 'Conclusion', children: [
    { id: 'lasting-impact', label: 'The Lasting Impact of a Refined Diamond' },
    { id: 'earth-to-elegance', label: 'From Earth to Elegance: A Story Complete' }
  ]}
];

const RoughToPolishPage: React.FC = () => {
  // Scroll spy: highlight current section in TOC.
  useEffect(() => {
    const headings = Array.from(document.querySelectorAll('[data-spy]')) as HTMLElement[];
    const tocLinks = Array.from(document.querySelectorAll('.process-toc a')) as HTMLAnchorElement[];

    const onScroll = () => {
      const offset = window.scrollY + 120; // account for fixed header
      let current: HTMLElement | null = null;
      for (const h of headings) {
        if (h.offsetTop <= offset) current = h; else break;
      }
      if (current) {
        tocLinks.forEach(l => l.classList.remove('active'));
        const active = tocLinks.find(l => l.getAttribute('href') === '#' + current.id);
        if (active) active.classList.add('active');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="process-article-page">
      {/* Hero */}
      <section className="process-hero gradient-bg">
        <Container>
          <div className="process-hero-top">
            <Link to="/" className="back-square" aria-label="Back to home">&larr; Back</Link>
          </div>
          <h1 className="process-hero-title">The Rough to Polish Cycle: A Diamond’s Journey from Earth to Elegance</h1>
          <div className="process-meta d-flex justify-content-between flex-wrap">
            <div className="byline">By SunDiam</div>
            <div className="date">{ARTICLE_DATE}</div>
          </div>
        </Container>
      </section>

      {/* Article Body */}
      <section className="process-body">
        <Container>
          <Row>
            <Col md={8} className="article-col">
              <article>
                {/* Integrated banner composite + captions */}
                <div className="banner-composite in-article">
                  <img src={bannerImg} alt="Illustration of mining to polished diamonds transformation" />
                </div>
                <p className="banner-caption">Beneath the earth’s surface, under immense pressure and unimaginable heat, something extraordinary takes shape. It’s not born of perfection — but of resilience, time, and transformation.</p>
                <p className="banner-caption">This is the story of a diamond.</p>
                <p className="banner-caption">Let’s walk through this incredible transformation, one phase at a time.</p>
                <h2 id="introduction" data-spy className="article-heading">Introduction</h2>
                <h3 id="timeless-allure" data-spy className="subheading">The Timeless Allure of Diamonds</h3>
                <p>Diamonds have long captured human imagination—symbols of endurance, purity, and everlasting connection. Their value extends beyond rarity; it’s the improbable journey from carbon under pressure to radiant brilliance that inspires awe.</p>
                <h3 id="what-is-cycle" data-spy className="subheading">What is the Rough to Polish Cycle?</h3>
                <p>The Rough to Polish Cycle narrates each decisive phase a diamond undergoes—from its raw geological origin to a finished gem of precision, symmetry, and sparkle. Understanding this cycle deepens appreciation for the craftsmanship and responsibility behind every stone.</p>

                <h2 id="discovery" data-spy className="article-heading">Discovery: Unearthing Earth's Hidden Gems</h2>
                <h3 id="where-found" data-spy className="subheading">Where Diamonds Are Found</h3>
                <p>Every diamond begins its story deep underground, where it forms over billions of years in extreme geological conditions. Hidden in kimberlite pipes or carried by rivers into alluvial deposits, rough diamonds are uncovered by miners who search through layers of ancient rock.</p>
                <h3 id="role-of-mining" data-spy className="subheading">The Role of Mining in the Journey</h3>
                <p>At this stage, the diamond is untouched, still wrapped in mystery—dull on the outside, but brimming with potential within. It’s nature’s raw blueprint, awaiting human vision and hands to uncover what lies beneath.</p>

                <h2 id="evaluation" data-spy className="article-heading">Evaluation: Revealing the Stone’s True Promise</h2>
                <h3 id="assessing-rough" data-spy className="subheading">How Gemologists Assess Rough Diamonds</h3>
                <p>Once unearthed, the rough diamond enters the hands of expert gemologists. These specialists analyze its physical qualities—carat weight, color, clarity, and internal structure—to determine its potential.</p>
                <h3 id="preview-4cs" data-spy className="subheading">The 4Cs: A Preview of Quality</h3>
                <p>This is not just technical; it’s part intuition, part science. It’s about seeing the brilliance not as it is, but as it could be.</p>
                <p>The evaluation phase is crucial. It informs how the diamond will be cut, shaped, and eventually brought to life. Every inclusion, every imperfection, becomes part of the plan. Nothing is wasted—only redirected.</p>

                <h2 id="planning" data-spy className="article-heading">Planning: Designing Brilliance with Precision</h2>
                <h3 id="imaging-modeling" data-spy className="subheading">Using 3D Imaging and Modeling</h3>
                <p>Using 3D imaging and digital modeling, artisans map out each facet, angle, and cut with astonishing precision. The goal: maximize beauty while preserving as much of the original weight as possible.</p>
                <h3 id="carat-vs-sparkle" data-spy className="subheading">Balancing Carat Retention and Sparkle</h3>
                <p>This stage is a beautiful blend of mathematics and artistry—a chess match between nature’s shape and human skill. Every decision made here affects how the diamond will reflect light, how it will sparkle, and ultimately, how it will be remembered.</p>

                <h2 id="cutting-polishing" data-spy className="article-heading">Cutting & Polishing: Bringing the Diamond to Life</h2>
                <h3 id="cleaving-bruting" data-spy className="subheading">Cleaving, Bruting, Faceting</h3>
                <p>Now begins the transformation. Master cutters carefully cleave or saw the stone, following the plan with absolute precision. They carve facets—tiny geometric surfaces—that catch and reflect light in just the right way.</p>
                <h3 id="role-of-artisans" data-spy className="subheading">The Role of Artisans in Final Brilliance</h3>
                <p>Then comes the polishing: the process that turns a shaped stone into a brilliant gem. It’s a delicate dance of speed, pressure, and angle—often taking days to complete. The polish determines how intensely the diamond sparkles, how it catches the eye and heart alike. Here, the diamond finds its voice.</p>

                <h2 id="certification" data-spy className="article-heading">Certification & Presentation: Honoring the Final Form</h2>
                <h3 id="grading-process" data-spy className="subheading">Diamond Grading and Certification Process</h3>
                <p>Finally, the polished diamond is sent to gemological institutes like GIA or IGI. There, it is graded and certified for its cut, clarity, color, and carat weight. These aren’t just stats—they’re the DNA of the diamond.</p>
                <h3 id="gem-to-jewelry" data-spy className="subheading">The Journey from Gem to Jewelry</h3>
                <p>Once certified, the diamond is ready to be set into fine jewelry or sold as a standalone gem. It stands now not just as a thing of beauty, but as a testament to nature’s power and human craftsmanship.</p>
                <ul className="checklist">
                  <li>It is no longer rough.</li>
                  <li>It is refined.</li>
                  <li>It is complete.</li>
                </ul>

                <h2 id="metaphor" data-spy className="article-heading">More Than a Process — A Metaphor for Growth</h2>
                <h3 id="life-lessons" data-spy className="subheading">Life Lessons from the Rough to Polish Cycle</h3>
                <p>The Rough to Polish Cycle isn’t just the story of a gemstone. It mirrors the journey many of us take in life—from uncertainty to clarity, from raw potential to shining purpose.</p>
                <h3 id="why-inspires" data-spy className="subheading">Why This Transformation Inspires Us All</h3>
                <p>We all begin rough—uncut and unrefined. Through pressure, shaping, feedback, and time, we polish our skills, our character, and our light.</p>
                <p>So the next time you see a diamond sparkle, remember: you’re witnessing the final step in an epic journey that started deep within the earth… And became something timeless.</p>

                <h2 id="conclusion" data-spy className="article-heading">Conclusion</h2>
                <h3 id="lasting-impact" data-spy className="subheading">The Lasting Impact of a Refined Diamond</h3>
                <p>A refined diamond is a union of nature and human mastery—an object of beauty that carries the imprint of every hand and decision that shaped it.</p>
                <h3 id="earth-to-elegance" data-spy className="subheading">From Earth to Elegance: A Story Complete</h3>
                <p>The Rough to Polish Cycle is more than production—it is transformation with purpose. From hidden crystal to radiant brilliance, its journey reflects our own potential for refinement, resilience, and lasting impact.</p>
              </article>
            </Col>
            <Col md={4} className="process-toc-wrapper">
              <nav className="process-toc" aria-label="Table of Content">
                <h5 className="toc-title">Table of Content</h5>
                <ul className="toc-root">
                  {TOC.map(item => (
                    <li key={item.id}>
                      <a href={`#${item.id}`}>{item.label}</a>
                      {item.children && (
                        <ul>
                          {item.children.map(c => (
                            <li key={c.id}><a href={`#${c.id}`}>{c.label}</a></li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default RoughToPolishPage;
